import DownloadIcon from '@mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProposalsApi } from '../../../api/proposals-api'
import GicBreadcrumbs, { BreadcrumbItem } from '../../shared/breadcrumbs'
import { FeatureFlagEnabled } from '../../shared/feature-flag-enabled'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
} from '../../shared/formats.ts'
import { PageMessage } from '../../shared/page-message'
import { ProposalFile, ProposalStatus, ScheduleType } from '../../shared/types'
import PayoutMethodInfobox from '../create-proposal/payout-method-infobox'
import { useIssuers } from '../issuers-context'
import { BidsTable, BidTableRow } from './bids-table'
import { CancelProposalDialog } from './cancel-proposal-dialog'
import { FinalizeSelectionDialog } from './finalize-selection-dialog'
import { ProposalDescriptionSection } from './proposal-description-section'
import './proposal-detail-page.scss'
import { ScenarioCards } from './scenario-cards'
import { SelectedBidsTable } from './selected-bids-table'
import useBuyerProposal from './use-buyer-proposal.ts'
import { useFlattenedBids } from './use-flattened-bids'
import { useScenarioNames } from './use-scenario-names'

export default function ProposalDetailPage() {
  const { id: proposalIdParam } = useParams<{ id: string }>()
  const {
    proposal,
    loading: loadingProposal,
    reload: reloadProposal,
  } = useBuyerProposal(proposalIdParam)
  const { scenarioNamesMap } = useScenarioNames(proposal?.scenarios || [])
  const {
    flattenedBids,
    flattenedSelectedBids,
    isLoading: bidsLoading,
  } = useFlattenedBids(proposal, scenarioNamesMap)
  const { issuers } = useIssuers()

  // Dialog state management
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false)
  const [selectedBids, setSelectedBids] = useState<BidTableRow[]>([])
  const bidsTableRef = useRef<AgGridReact<BidTableRow>>(null)

  // Loading states
  const [isCancelling, setIsCancelling] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({ open: false, message: '', severity: 'info' })

  // Schedule type state for viewing
  // Get the original schedule type from the proposal, defaulting to DEPOSIT if not set
  const originalScheduleType = useMemo(
    () => proposal?.scheduleType ?? ScheduleType.DEPOSIT,
    [proposal]
  )
  const [viewScheduleType, setViewScheduleType] =
    useState<ScheduleType>(originalScheduleType)

  // Update view schedule type when proposal changes
  useMemo(() => {
    setViewScheduleType(originalScheduleType)
  }, [originalScheduleType])

  // Get selected bids from the ag-grid table
  const getSelectedBids = useCallback((): BidTableRow[] => {
    if (bidsTableRef.current?.api) {
      return bidsTableRef.current.api.getSelectedRows()
    }
    return []
  }, [])

  // Update selectedBids state in real-time when custom checkbox selection changes
  const handleSelectionChanged = useCallback(
    (rawSelectedRows: BidTableRow[]) => {
      setSelectedBids(rawSelectedRows)
    },
    []
  )

  const handleCancelProposal = useCallback(async () => {
    if (proposalIdParam === undefined || isCancelling) return

    setIsCancelling(true)
    try {
      const success = await ProposalsApi.updateProposalStatus(
        proposalIdParam,
        ProposalStatus.CANCELLED
      )
      if (success) {
        setCancelDialogOpen(false)
        setSnackbar({
          open: true,
          message: 'Proposal cancelled successfully',
          severity: 'success',
        })
        // Refresh data after short delay to show success message
        setTimeout(() => {
          void reloadProposal()
        }, 1500)
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to cancel proposal. Please try again.',
          severity: 'error',
        })
      }
    } catch (error) {
      console.error('Error cancelling proposal:', error)
      setSnackbar({
        open: true,
        message: 'An error occurred while cancelling the proposal.',
        severity: 'error',
      })
    }
  }, [proposalIdParam, isCancelling, reloadProposal])

  const handleFinalizeSelection = useCallback(
    async (
      finalizedBids: {
        scenarioId: string
        issuerTenantId: string
        rate: number
        treasuryRate: number
        amount: number
        wamYears: number | null
        partialMaturities: {
          paymentDate: Date | string | null
          amount: number
          inputType: string
        }[]
      }[]
    ) => {
      if (!proposalIdParam || isFinalizing) return

      setIsFinalizing(true)
      try {
        const selectedBidsSuccess =
          await ProposalsApi.createSelectedBids(finalizedBids)
        if (!selectedBidsSuccess) {
          setSnackbar({
            open: true,
            message: 'Failed to save selected bids. Please try again.',
            severity: 'error',
          })
          return
        }

        const statusSuccess = await ProposalsApi.updateProposalStatus(
          proposalIdParam,
          ProposalStatus.COMPLETED
        )
        if (statusSuccess) {
          setFinalizeDialogOpen(false)
          setSnackbar({
            open: true,
            message: 'Proposal finalized successfully',
            severity: 'success',
          })
          // Refresh data after short delay to show success message
          setTimeout(() => {
            void reloadProposal()
          }, 1500)
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to complete proposal. Please try again.',
            severity: 'error',
          })
        }
      } catch (error) {
        console.error('Error finalizing selection:', error)
        setSnackbar({
          open: true,
          message: 'An error occurred while finalizing the selection.',
          severity: 'error',
        })
      }
    },
    [proposalIdParam, isFinalizing, reloadProposal]
  )

  const handleFinalizeButtonClick = useCallback(() => {
    const selected = getSelectedBids()
    if (selected.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one bid to finalize.',
        severity: 'warning',
      })
      return
    }

    setSelectedBids(selected)
    setFinalizeDialogOpen(true)
  }, [getSelectedBids])

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  const handleDescriptionSnackbar = useCallback(
    (message: string, severity: 'success' | 'error') => {
      setSnackbar({ open: true, message, severity })
    },
    []
  )

  // Memoized values for finalize button state
  const selectedBidsCount = useMemo(() => selectedBids.length, [selectedBids])
  const isFinalizeDisabled = useMemo(() => {
    return selectedBidsCount === 0 || isCancelling || isFinalizing
  }, [selectedBidsCount, isCancelling, isFinalizing])

  const participationData = useMemo(() => {
    if (!proposal || !issuers || issuers.length === 0) {
      return {
        total: 0,
        responded: 0,
        pendingIssuers: [],
      }
    }

    const total = proposal.issuerTenantIds.length
    const respondedIssuerIds = new Set(
      flattenedBids.map((bid) => bid.issuerTenantId)
    )
    const responded = respondedIssuerIds.size

    // Get names of pending issuers
    const pendingIssuers = proposal.issuerTenantIds
      .filter((tenantId) => !respondedIssuerIds.has(tenantId))
      .map((tenantId) => {
        const issuer = issuers.find((i) => i.issuerTenantId === tenantId)
        return issuer?.displayName || 'Unknown Issuer'
      })

    return {
      total,
      responded,
      pendingIssuers,
    }
  }, [proposal, issuers, flattenedBids])

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    if (!proposal) return [{ label: 'Proposals', path: '/proposals' }]

    const bidDeadlineLabel = proposal.bidDeadline
      ? new Date(proposal.bidDeadline).toLocaleDateString()
      : ''

    return [
      { label: 'Proposals', path: '/proposals' },
      { label: `${proposal.fund} – ${bidDeadlineLabel}` },
    ]
  }, [proposal])

  const extractFileName = (file: ProposalFile) => file.name

  const handleDownload = (file: ProposalFile) => () => {
    void ProposalsApi.downloadFile(file.id, file.name)
  }

  if (loadingProposal) {
    return <PageMessage message="Loading..." />
  }

  if (!proposal) {
    return <PageMessage message="Proposal not found" />
  }

  return (
    <Box className="proposal-detail-container">
      <Box className="proposal-detail-header">
        <GicBreadcrumbs items={breadcrumbItems} />
      </Box>
      <Box className="proposal-detail-content">
        <Box>
          <Typography variant="h5" className="section-title">
            Basic Information
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Fund
              </Typography>
              <Typography variant="body1">{proposal.fund}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Bid Deadline
              </Typography>
              <Typography variant="body1">
                {formatDateTime(proposal.bidDeadline)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Status
              </Typography>
              <Typography variant="body1">{proposal.status}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Deposit Amount
              </Typography>
              <Typography variant="body1">
                {formatCurrency(proposal.depositAmount)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Typography variant="subtitle2" className="info-card-label">
                Deposit Date
              </Typography>
              <Typography variant="body1">
                {formatDate(proposal.depositDate)}
              </Typography>
            </Grid>
          </Grid>

          <FeatureFlagEnabled flag="ENABLE_PROPOSAL_DESCRIPTIONS">
            <Box sx={{ mt: '2rem' }}>
              <ProposalDescriptionSection
                proposalId={proposalIdParam ?? ''}
                description={proposal.description}
                onUpdate={() => void reloadProposal()}
                onShowSnackbar={handleDescriptionSnackbar}
              />
            </Box>
          </FeatureFlagEnabled>
        </Box>

        {proposal.files && proposal.files.length > 0 && (
          <>
            <Divider className="divider-spacing" />
            <Box>
              <Typography variant="h5" className="section-title">
                Attached Files
              </Typography>
              <Card variant="outlined" className="files-card">
                <Table size="small">
                  <TableBody>
                    {proposal.files.map((file) => (
                      <TableRow key={file.id} className="file-row">
                        <TableCell component="th" scope="row">
                          {extractFileName(file)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={handleDownload(file)}
                          >
                            <DownloadIcon
                              fontSize="small"
                              className="download-icon"
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          </>
        )}

        {proposal.scenarios && proposal.scenarios.length > 0 && (
          <>
            <Divider className="divider-spacing" />
            <Box>
              <Typography variant="h5" className="section-title">
                Scenarios
              </Typography>
              <FeatureFlagEnabled flag="ENABLE_PARTIAL_MATURITIES">
                <PayoutMethodInfobox
                  scheduleType={viewScheduleType}
                  onScheduleTypeChange={setViewScheduleType}
                  visible={true}
                  showRoundingWarning={false}
                  originalScheduleType={originalScheduleType}
                />
              </FeatureFlagEnabled>
              <ScenarioCards
                scenarios={proposal.scenarios}
                scenarioNamesMap={scenarioNamesMap}
                viewScheduleType={viewScheduleType}
                originalScheduleType={originalScheduleType}
              />
            </Box>
          </>
        )}

        <Divider className="divider-spacing" />

        {proposal && (
          <Box>
            <Typography variant="h5" className="section-title">
              Bids
            </Typography>
            <BidsTable
              ref={bidsTableRef}
              bids={flattenedBids}
              isLoading={bidsLoading}
              showCheckboxes={proposal.status === ProposalStatus.ACTIVE}
              selectedRows={selectedBids}
              onSelectionChanged={handleSelectionChanged}
              participationData={participationData}
            />
          </Box>
        )}

        {/* Selected Bids Table - Show when proposal is not active */}
        {proposal &&
          proposal.status !== ProposalStatus.ACTIVE &&
          proposal.scenarios.some(
            (scenario) => scenario.selectedBids.length > 0
          ) && (
            <>
              <Divider className="divider-spacing" />
              <Box>
                <Typography variant="h5" className="section-title">
                  Selected bids
                </Typography>
                <SelectedBidsTable
                  selectedBids={flattenedSelectedBids}
                  isLoading={bidsLoading || loadingProposal}
                />
              </Box>
            </>
          )}
      </Box>

      {/* Action Buttons - Show only when proposal status is ACTIVE */}
      {proposal && proposal.status === ProposalStatus.ACTIVE && (
        <Box className="action-buttons-container">
          <Button
            variant="outlined"
            onClick={() => setCancelDialogOpen(true)}
            size="small"
            disabled={isCancelling || isFinalizing}
          >
            Cancel Proposal
          </Button>
          <Tooltip
            title={
              selectedBidsCount === 0
                ? 'Please select at least one bid to finalize'
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinalizeButtonClick}
                size="small"
                disabled={isFinalizeDisabled}
              >
                Finalize Selection
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Dialogs */}
      <CancelProposalDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false)
          setIsCancelling(false)
        }}
        onConfirm={() => void handleCancelProposal()}
        proposalFund={proposal?.fund || ''}
        bidDeadline={proposal?.bidDeadline || ''}
        issuerCount={proposal?.issuerTenantIds?.length || 0}
        isLoading={isCancelling}
      />

      <FinalizeSelectionDialog
        isOpen={finalizeDialogOpen}
        onClose={() => {
          setFinalizeDialogOpen(false)
          setIsFinalizing(false)
        }}
        onFinalize={(bids) => void handleFinalizeSelection(bids)}
        selectedBids={selectedBids}
        scenarios={proposal?.scenarios || []}
        proposalDepositAmount={proposal?.depositAmount || 0}
        isLoading={isFinalizing}
        scheduleType={originalScheduleType}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          className="alert-full-width"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
